<template>
  <div :class="{ 'modal-backdrop': !isEmbedded }" @click.self="!isEmbedded && close()">
    <div :class="{ 'modal-content': !isEmbedded }">
      <header v-if="!isEmbedded" class="modal-header">
        <h2>{{ isEditMode ? 'Edit Customer' : 'Add New Customer' }}</h2>
        <button @click="close" class="btn-close">&times;</button>
      </header>
      <section :class="{ 'modal-body': !isEmbedded }">
        <form @submit.prevent="handleSubmit" class="booking-form">
          <div class="form-row">
            <div class="form-group">
              <label for="firstName">First Name *</label>
              <input type="text" id="firstName" v-model="form.firstName" required>
            </div>
            <div class="form-group">
              <label for="lastName">Last Name</label>
              <input type="text" id="lastName" v-model="form.lastName">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="email">Email</label>
              <input type="email" id="email" v-model="form.email">
            </div>
            <div class="form-group">
              <label for="phoneNumber">Phone Number</label>
              <input type="tel" id="phoneNumber" v-model="form.phoneNumber">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="dob">Date of Birth</label>
              <input type="date" id="dob" v-model="form.dob">
            </div>
            <div class="form-group">
              <label for="gender">Gender</label>
              <select id="gender" v-model="form.gender">
                <option :value="undefined">Select...</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
           <div class="form-group">
            <label for="address">Address</label>
            <textarea id="address" v-model="form.address" rows="3"></textarea>
          </div>
          <div class="form-group">
            <label for="notes">Notes (Allergies, Preferences)</label>
            <textarea id="notes" v-model="form.notes" rows="3"></textarea>
          </div>
          <footer class="form-footer" :class="{ 'modal-footer': !isEmbedded }">
            <button v-if="!isEmbedded" type="button" class="btn btn-secondary" @click="close">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
              {{ isSubmitting ? 'Saving...' : 'Save Customer' }}
            </button>
          </footer>
        </form>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, PropType, computed } from 'vue';
import type { Customer } from '@/types/customer.types';
import * as customerService from '@/services/customer.service';
import { useToast } from '@/composables/useToast';

type CustomerFormData = Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'last_visit_date'>;

export default defineComponent({
  name: 'CustomerForm',
  props: {
    customer: {
      type: Object as PropType<Partial<Customer> | null>,
      default: null,
    },
    isEmbedded: {
      type: Boolean,
      default: false,
    },
    isPublicBooking: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'save-success', 'customer-selected'],
  setup(props, { emit }) {
    const toast = useToast();
    const form = ref<Partial<CustomerFormData>>({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      dob: '',
      gender: undefined,
      address: '',
      notes: '',
      status: 'active',
      totalVisit: 0
    });

    const isSubmitting = ref(false);
    const isEditMode = computed(() => !!props.customer?.id);

    watch(() => props.customer, (newCustomer) => {
      if (newCustomer) {
        form.value = {
          ...newCustomer,
          dob: newCustomer.dob ? newCustomer.dob.split('T')[0] : '',
        };
      } else {
        // Reset form for 'add' mode
        Object.keys(form.value).forEach(key => (form.value as any)[key] = '');
        form.value.status = 'active';
      }
    }, { immediate: true });

    const close = () => {
      emit('close');
    };

    const handleSubmit = async () => {
      isSubmitting.value = true;
      try {
        const dataToSave = JSON.parse(JSON.stringify(form.value));
        dataToSave.totalVisit = 0;
        if (!dataToSave.dob) delete dataToSave.dob;
        if (!dataToSave.gender) delete dataToSave.gender;
        if (!dataToSave.email) delete dataToSave.email;
        let savedCustomer;
        if (isEditMode.value && props.customer?.id) {
          savedCustomer = await customerService.updateCustomer(props.customer!.id, dataToSave);
        } else if (props.isPublicBooking) {
          savedCustomer = await customerService.addPublicCustomer(dataToSave);
        } else {
          savedCustomer = await customerService.addCustomer(dataToSave);
        }
        emit('save-success', savedCustomer);
      } catch (error) {
        toast.error('Failed to save customer details. Please try again.');
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      form,
      isEditMode,
      isSubmitting,
      close,
      handleSubmit,
    };
  },
});
</script>
<style scoped>
/* Styles are extensive, keeping them the same for brevity */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  z-index: 1000;
  padding-top: 5rem;
}

.modal-content {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
  width: 90%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 25px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
}

.modal-body {
  padding: 25px;
  overflow-y: auto;
  max-height: 70vh;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 12px;
}

.form-row .form-group {
  flex: 1 1 200px;
  margin-right: 20px;
  margin-bottom: 0;
}

.booking-form > .form-group {
  margin-bottom: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-group label {
  margin-bottom: 5px;
  font-weight: 600;
  font-size: 0.9rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 15px 25px;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 10px 20px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  font-weight: bold;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  margin-right: 10px;
}

.btn-primary {
  background-color: var(--brand-gold);
  color: var(--brand-black);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.search-results {
  list-style: none;
  padding: 0;
  margin: 10px 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  max-height: 150px;
  overflow-y: auto;
}

.search-results li {
  padding: 10px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-results li:hover {
  background-color: #f1f1f1;
}

.search-results li small {
  color: #6c757d;
}

.new-customer-prompt {
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px dashed #ddd;
}

@media (max-width: 768px) {
  .booking-form .form-row {
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 8px;
    margin-bottom: 8px;
  }

  .booking-form .form-row .form-group {
    flex: none;
    width: 100%;
  }

  .booking-form > .form-group {
    margin-bottom: 8px;
  }

  .form-footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
    padding-top: 0;
    border-top: none;
  }

  .form-footer .btn {
    width: 100%;
    margin: 0 !important;
    min-height: 40px;
    padding: 8px 16px;
  }

  .modal-backdrop {
    padding: 0.5rem;
    padding-top: 4rem;
  }

  .modal-body {
    padding: 16px;
    max-height: 65vh;
  }

  .modal-footer {
    flex-direction: column-reverse;
    gap: 8px;
    margin-top: 10px;
    padding: 12px 16px;
  }

  .modal-footer .btn {
    width: 100%;
    margin-right: 0 !important;
    min-height: 40px;
  }
}
</style>